/**
 * ICE failure guard for JsSIP sessions.
 *
 * JsSIP terminates a session on `iceConnectionState === 'failed'`
 * (RTCSession.js `_createRTCConnection`), but that transition only fires when
 * candidate pairs EXIST and every check fails. When ICE gathering produces
 * ZERO local candidates — `iceTransportPolicy: 'relay'` with no reachable
 * TURN server, no usable network interface, or a dead STUN/TURN path — no
 * pair can ever form and the state sits at 'new'/'checking' forever. The SIP
 * dialog still completes (INVITE → 200 OK → ACK) and the UI shows a
 * connected call with a dead media path: the zombie-call defect from
 * TSV-03-0008.
 *
 * This guard covers the two holes JsSIP leaves:
 *   1. Gathering completed with zero candidates → terminate immediately. No
 *      local candidates means no media path regardless of what the far end
 *      offers, so there is nothing to wait for.
 *   2. ICE entered 'checking' (remote credentials arrived) but never reached
 *      'connected'/'completed' within `settleMs` → terminate. This catches
 *      exhausted-checklist states that never surface as 'failed' on some
 *      builds, plus permanently blackholed paths.
 *
 * 'disconnected' is deliberately NOT terminal — Chrome recovers transient
 * disconnects via consent freshness / new pairs on its own. JsSIP's own
 * handler still covers the genuine 'failed' transition; the guard's
 * iceconnectionstate listener is belt-and-suspenders for the same state.
 */
export interface IceFailureGuardOptions {
  /** How long after ICE enters 'checking' to wait for a connected state. */
  settleMs?: number
  /** How long after ICE connects to wait for DTLS to reach 'connected'. */
  dtlsSettleMs?: number
  /** Called once, immediately before the session is terminated. */
  onTerminate?: (reason: string) => void
}

interface TerminatableSession {
  terminate: () => void
}

export function attachIceFailureGuard(
  session: TerminatableSession,
  pc: RTCPeerConnection,
  opts: IceFailureGuardOptions = {},
): void {
  const settleMs = opts.settleMs ?? 10_000
  const dtlsSettleMs = opts.dtlsSettleMs ?? 20_000
  let sawCandidate = false
  let terminated = false
  let checkTimer: ReturnType<typeof setTimeout> | null = null
  let dtlsTimer: ReturnType<typeof setTimeout> | null = null

  const terminate = (reason: string) => {
    if (terminated) return
    terminated = true
    if (checkTimer) clearTimeout(checkTimer)
    if (dtlsTimer) clearTimeout(dtlsTimer)
    opts.onTerminate?.(reason)
    try {
      session.terminate()
    } catch {
      // Session already terminated (or never fully established) — either way
      // the call is being torn down, which is the goal.
    }
  }

  pc.addEventListener('icecandidate', (ev) => {
    if (ev.candidate) sawCandidate = true
  })

  pc.addEventListener('icegatheringstatechange', () => {
    if (pc.iceGatheringState === 'complete' && !sawCandidate) {
      terminate('ice-gathering-complete-with-zero-candidates')
    }
  })

  pc.addEventListener('iceconnectionstatechange', () => {
    if (terminated) return
    const state = pc.iceConnectionState
    if (state === 'checking' && checkTimer === null) {
      checkTimer = setTimeout(() => {
        checkTimer = null
        if (terminated) return
        const s = pc.iceConnectionState
        // 'closed' is the PC being torn down by its owner — not a failure.
        if (s === 'connected' || s === 'completed' || s === 'closed') return
        terminate('ice-never-connected')
      }, settleMs)
      return
    }
    // A successful connection discharges the watchdog; a fresh 'checking'
    // later (ICE restart via re-INVITE) re-arms it.
    if (state === 'connected' || state === 'completed' || state === 'closed') {
      if (checkTimer) {
        clearTimeout(checkTimer)
        checkTimer = null
      }
      // ICE up but DTLS stuck is the same zombie shape (e.g. fingerprint
      // mismatch): connectionState can even report 'connected' while the
      // handshake never finishes, so verify dtlsState through getStats.
      if ((state === 'connected' || state === 'completed') && dtlsTimer === null) {
        dtlsTimer = setTimeout(() => {
          dtlsTimer = null
          if (terminated) return
          void pc
            .getStats()
            .then((stats) => {
              let sawTransport = false
              let dtlsUp = false
              stats.forEach((report: any) => {
                if (report.type !== 'transport') return
                sawTransport = true
                if (report.dtlsState === 'connected' || report.dtlsState === 'closed') dtlsUp = true
              })
              if (sawTransport && !dtlsUp) terminate('dtls-not-established')
            })
            .catch(() => {})
        }, dtlsSettleMs)
      }
      return
    }
    if (state === 'failed') {
      terminate('ice-connection-failed')
    }
  })

  // connectionState='failed' also covers DTLS-layer death (e.g. a fingerprint
  // mismatch): the ICE pair can stay 'connected' while DTLS never completes,
  // which iceConnectionState alone never surfaces.
  pc.addEventListener('connectionstatechange', () => {
    if (pc.connectionState === 'failed') {
      terminate('connection-state-failed')
    }
  })
}
